import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/application/services/authService";
import { useNavigationService } from "@/application/services/navigationService";
import { useMenuService } from "@/application/services/menuService";
import { Avatar } from "@/ui/components/common/Avatar";
import { ROUTES } from "@/domain/constants/routes";

interface UserMenuProps {
  isMobile?: boolean;
}

/**
 * 用戶菜單組件
 * 顯示用戶相關操作的下拉菜單
 */
export function UserMenu({ isMobile = false }: UserMenuProps) {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const { getUserMenuItems } = useNavigationService();
  const {
    isUserMenuOpen,
    toggleUserMenu,
    closeAllMenus,
    menuRef,
    menuButtonRef,
  } = useMenuService();

  if (!isAuthenticated) {
    return (
      <Link
        to={ROUTES.LOGIN}
        className={`${isMobile ? "block" : ""} px-3 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300`}
        onClick={closeAllMenus}
      >
        {t("auth.login")}
      </Link>
    );
  }

  if (isMobile) {
    return (
      <div className="space-y-3">
        {/* 用戶信息 */}
        <div className="flex items-center px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-t-md">
          <div className="mr-3">
            <Avatar
              src={user?.profile_picture_url}
              alt={user?.name || t("user.profile")}
              size="md"
              fallbackText={user?.name || "?"}
            />
          </div>
          <div>
            <div className="text-base font-medium text-gray-800 dark:text-gray-200">
              {user?.name || t("user.profile")}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {user?.email || ""}
            </div>
          </div>
        </div>

        {/* 用戶選項 */}
        <div className="px-2">
          {getUserMenuItems().map((item, index) =>
            item.path ? (
              <Link
                key={index}
                to={item.path}
                className={`flex items-center rounded-md px-4 py-3 my-1 text-base ${
                  item.variant === "danger"
                    ? "text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
                onClick={closeAllMenus}
              >
                {item.label}
              </Link>
            ) : (
              <button
                key={index}
                className={`flex items-center w-full text-left rounded-md px-4 py-3 my-1 text-base ${
                  item.variant === "danger"
                    ? "text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
                onClick={item.action ? item.action : undefined}
              >
                {item.label}
              </button>
            ),
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={menuButtonRef}>
      <button
        onClick={toggleUserMenu}
        aria-label={t("user.menu")}
        title={t("user.menu")}
        className="flex items-center p-1 rounded-full hover:ring-2 hover:ring-gray-200 dark:hover:ring-gray-700 transition-all duration-200"
      >
        <Avatar
          src={user?.profile_picture_url}
          alt={user?.name || t("user.profile")}
          size="sm"
          fallbackText={user?.name || "?"}
        />
      </button>

      {/* 用戶選單 */}
      {isUserMenuOpen && (
        <div
          ref={menuRef}
          className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg overflow-hidden z-50 border border-gray-200 dark:border-gray-700 animate-fadeIn"
          style={{ animationDuration: "150ms" }}
        >
          {/* 用戶信息頭部 */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <div className="flex items-center">
              <div className="mr-3">
                <Avatar
                  src={user?.profile_picture_url}
                  alt={user?.name || t("user.profile")}
                  size="sm"
                  fallbackText={user?.name || "?"}
                />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  {user?.name || t("user.profile")}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {user?.email || ""}
                </div>
              </div>
            </div>
          </div>

          {/* 選單項目 */}
          <div className="py-1">
            {getUserMenuItems().map((item, index) =>
              item.path ? (
                <Link
                  key={index}
                  to={item.path}
                  className={`flex items-center px-4 py-2 text-sm ${
                    item.variant === "danger"
                      ? "text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  }`}
                  onClick={closeAllMenus}
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={index}
                  className={`flex items-center w-full text-left px-4 py-2 text-sm ${
                    item.variant === "danger"
                      ? "text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  }`}
                  onClick={item.action ? item.action : undefined}
                >
                  {item.label}
                </button>
              ),
            )}
          </div>
        </div>
      )}
    </div>
  );
}
